import React, { useState, useMemo } from 'react';
import { MdPeople, MdPhoneIphone, MdDoneAll } from 'react-icons/md';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Tabs from '../../ui/Tabs';
import { typeLabel, typeTone, describeAudience } from './NotificationCompose';
import './notifications.css';

/**
 * One send, opened up: the three numbers at the top, then the full
 * recipient list split into read / not-read.
 *
 * This is the screen the whole batch model exists for. "312 people
 * were notified" is only believable if you can click it and see the
 * 312 names, which of them opened it, and when — otherwise the number
 * is just a claim.
 *
 * Note what is NOT claimed here: nothing says "delivered". The system
 * knows who it wrote a notification for (exact) and how many of their
 * devices had push enabled at send time (exact). Whether a phone
 * actually displayed it is knowledge only the push provider has, so
 * the UI never pretends to it. "قرأ" is the honest signal, and the one
 * a manager actually wants.
 */

const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');

export default function NotificationDetail({ batch, recipients, loading, refData, onClose }) {
  const [tab, setTab] = useState('all');

  const rows = useMemo(() => recipients || [], [recipients]);
  const readRows = useMemo(() => rows.filter((r) => r.is_read), [rows]);
  const unreadRows = useMemo(() => rows.filter((r) => !r.is_read), [rows]);

  const shown = tab === 'read' ? readRows : tab === 'unread' ? unreadRows : rows;

  const columns = [
    { key: 'name', label: 'الاسم', width: 2 },
    // For a parent row this says which child the message was about.
    { key: 'student_name', label: 'الطالب', width: 2, formatValue: (v) => v || '—' },
    {
      key: 'recipient_role',
      label: 'الصفة',
      nowrap: true,
      formatValue: (v) => ({ student: 'طالب', parent: 'ولي أمر', teacher: 'معلم' }[v] || v || '—'),
      badge: () => 'neutral',
    },
    { key: 'section_label', label: 'الشعبة', width: 2 },
    {
      key: 'has_device',
      label: 'جهاز',
      nowrap: true,
      align: 'center',
      formatValue: (v) => (v ? 'نعم' : 'لا'),
      badge: (v) => (v ? 'info' : 'neutral'),
      exportValue: (row) => (row.has_device ? 'نعم' : 'لا'),
    },
    {
      key: 'is_read',
      label: 'الحالة',
      nowrap: true,
      formatValue: (v) => (v ? 'قرأ' : 'لم يقرأ'),
      badge: (v) => (v ? 'success' : 'warning'),
      exportValue: (row) => (row.is_read ? 'قرأ' : 'لم يقرأ'),
    },
    { key: 'read_at', label: 'وقت القراءة', nowrap: true, formatValue: fmt },
  ];

  const readPct = rows.length ? Math.round((readRows.length / rows.length) * 100) : 0;

  return (
    <Modal
      open
      size="lg"
      title="تفاصيل الإشعار"
      onClose={onClose}
      footer={<Button variant="secondary" onClick={onClose}>إغلاق</Button>}
    >
      <div className="notif-detail-head">
        <span className={`ui-badge ui-badge-${typeTone(batch?.type)}`}>{typeLabel(batch?.type)}</span>
        <h3>{batch?.title}</h3>
        <p className="notif-detail-message">{batch?.message}</p>
        <div className="notif-detail-meta">
          <span>{describeAudience(batch?.audience, refData)}</span>
          <span>·</span>
          <span>أرسله {batch?.created_by_name || '—'}</span>
          <span>·</span>
          <span>{fmt(batch?.created_at)}</span>
        </div>
      </div>

      <div className="notif-review-stats">
        <div className="notif-stat">
          <MdPeople size={20} />
          <strong>{batch?.recipient_count ?? rows.length}</strong>
          <span>مستلم</span>
        </div>
        <div className="notif-stat">
          <MdPhoneIphone size={20} />
          <strong>{batch?.device_count ?? '—'}</strong>
          <span>جهاز</span>
        </div>
        <div className="notif-stat is-success">
          <MdDoneAll size={20} />
          <strong>{batch?.read_count ?? readRows.length}</strong>
          <span>قرأ ({readPct}%)</span>
        </div>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        items={[
          { id: 'all', label: 'الكل', badge: rows.length },
          { id: 'read', label: 'قرأ', badge: readRows.length },
          { id: 'unread', label: 'لم يقرأ', badge: unreadRows.length },
        ]}
      />

      <Table
        columns={columns}
        rows={shown}
        keyField="id"
        loading={loading}
        emptyText="لا يوجد مستلمون"
        searchPlaceholder="ابحث بالاسم..."
        searchableKeys={['name', 'student_name', 'section_label']}
        pageSize={10}
        exportName={`notification-${batch?.id || ''}`}
      />
    </Modal>
  );
}
